// What a gated screen shows when its feature is not in the business's plan
// (docs/plan-limits-plan.md §7 — lock, never hide). The owner gets a way to
// upgrade; everyone else is told who can. Platform users never see it:
// usePlanFeature() never locks them.

import { useNavigate } from 'react-router-dom';
import { PLAN_FEATURES, type PlanFeatureKey } from '@bbpos/types';
import { Button, PageContainer } from '@bbpos/ui';
import { useAuthStore } from '@/stores/use-auth-store';

export function LockedFeature({ feature }: { feature: PlanFeatureKey }) {
  const navigate = useNavigate();
  const isOwner = useAuthStore((s) => s.profile?.role === 'OWNER');
  const { label, description } = PLAN_FEATURES.find((f) => f.key === feature)!;

  return (
    <PageContainer>
      <div
        className="rounded-2xl border border-brand-100 bg-gradient-to-br from-brand-50 via-white to-indigo-50 p-10 shadow-md animate-fade-in dark:border-neutral-700 dark:from-brand-900/20 dark:via-neutral-900 dark:to-indigo-900/20"
        data-testid="locked-feature"
      >
        <div className="mx-auto flex max-w-md flex-col items-center text-center">
          <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-brand-500 to-indigo-500 text-white shadow-lg">
            <svg className="h-8 w-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5} aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          </div>
          <span className="mb-3 inline-flex items-center rounded-full bg-warning-100 px-3 py-1 text-xs font-semibold text-warning-700 dark:bg-warning-900/30 dark:text-warning-300">
            Locked
          </span>
          <p className="text-xl font-bold text-neutral-900 dark:text-white">{label}</p>
          <p className="mt-2 text-sm text-neutral-600 dark:text-neutral-300">{description}</p>
          <p className="mt-4 font-medium text-neutral-900 dark:text-white">This is not in your plan.</p>
          {isOwner ? (
            <Button variant="primary" className="mt-5" onClick={() => navigate('/my-subscription')}>
              Upgrade
            </Button>
          ) : (
            <p className="mt-2 text-sm text-neutral-500 dark:text-neutral-400">Ask your owner to upgrade.</p>
          )}
        </div>
      </div>
    </PageContainer>
  );
}
